import { supabase } from './supabase.js';

/** Roles que pueden vender entradas (alineado con migración SQL). */
export const PARTNER_TICKET_SELLER_ROLES = ['promoter', 'venue'];

let globalEnabledCache = { at: 0, value: false };

/**
 * Flag global `app_config.partner_ticket_sales.enabled`.
 * @returns {Promise<boolean>}
 */
export async function fetchPartnerTicketSalesGloballyEnabled() {
  const now = Date.now();
  if (now - globalEnabledCache.at < 45_000) return globalEnabledCache.value;
  const { data, error } = await supabase.from('app_config').select('value').eq('key', 'partner_ticket_sales').maybeSingle();
  if (error) {
    console.warn('[partnerTicketSales] global config:', error.message);
    return false;
  }
  const enabled = data?.value?.enabled === true;
  globalEnabledCache = { at: now, value: enabled };
  return enabled;
}

/**
 * Comprueba si el usuario puede usar venta de entradas (partner aprobado + suscripción + global).
 * @param {string} userId — auth.uid / profiles.id
 * @returns {Promise<{ eligible: boolean, globalEnabled: boolean, partner: object|null, reason: string }>}
 */
export async function fetchPartnerTicketSalesEligibility(userId) {
  const uid = String(userId ?? '').trim();
  const globalEnabled = await fetchPartnerTicketSalesGloballyEnabled();
  if (!globalEnabled) {
    return {
      eligible: false,
      globalEnabled: false,
      partner: null,
      reason: 'La venta de entradas está desactivada en DreamVinyl.',
    };
  }
  if (!uid) {
    return { eligible: false, globalEnabled: true, partner: null, reason: 'Inicia sesión como partner.' };
  }

  const { data: partner, error } = await supabase
    .from('partners')
    .select(
      'id, code, company_name, verification_status, is_active, ticket_sales_enabled, ticket_sales_subscription_active, partner_roles ( role, role_status, is_active )'
    )
    .eq('profile_id', uid)
    .maybeSingle();

  if (error) {
    console.warn('[partnerTicketSales] partner fetch:', error.message);
    return { eligible: false, globalEnabled: true, partner: null, reason: 'No se pudo comprobar tu cuenta partner.' };
  }
  if (!partner) {
    return {
      eligible: false,
      globalEnabled: true,
      partner: null,
      reason: 'Solo partners verificados con suscripción pueden vender entradas.',
    };
  }
  if (partner.verification_status !== 'approved' || partner.is_active === false) {
    return {
      eligible: false,
      globalEnabled: true,
      partner,
      reason: 'Tu cuenta partner debe estar aprobada y activa.',
    };
  }
  if (!partner.ticket_sales_enabled) {
    return {
      eligible: false,
      globalEnabled: true,
      partner,
      reason: 'DreamVinyl aún no ha activado la venta de entradas para tu partner.',
    };
  }
  if (!partner.ticket_sales_subscription_active) {
    return {
      eligible: false,
      globalEnabled: true,
      partner,
      reason: 'Se requiere suscripción activa de venta de entradas (contacta con DreamVinyl).',
    };
  }
  const hasRole = (partner.partner_roles || []).some(
    r =>
      PARTNER_TICKET_SELLER_ROLES.includes(r.role) &&
      r.role_status === 'active' &&
      r.is_active !== false
  );
  if (!hasRole) {
    return {
      eligible: false,
      globalEnabled: true,
      partner,
      reason: 'Necesitas rol Promotor o Venue/Sala activo en tu partner.',
    };
  }

  return { eligible: true, globalEnabled: true, partner, reason: '' };
}

/** Invalida caché tras cambio admin. */
export function invalidatePartnerTicketSalesConfigCache() {
  globalEnabledCache = { at: 0, value: false };
}
